import { formatWeight, isPatientAtRisk } from '@allurion/domain';
import { Card, InfoIcon, Tooltip } from '@allurion/ui';
import { isNotNull, toLocalizedLongDate } from '@allurion/utils';
import { useIntl } from 'react-intl';

import { EditStartingWeight } from 'src/components/WeightEntry/EditStartingWeight';
import { PatientOverview, PatientProfile } from 'src/domain/patient/Patients';
import { getContentLocaleOrFallback } from 'src/helpers/locale';
import { useCurrentProvider } from 'src/hooks/useCurrentProvider';
import { useLocale } from 'src/hooks/useLocale';
import translations from 'src/messages/translations';

import { SuccessPredictorCard } from './SuccessPredictorCard';

import styles from './PatientProgressOverviewCard.module.scss';

const contentfulValidLocales = [
  'en-GB',
  'ar',
  'ca',
  'zh-CN',
  'zh-Hant-TW',
  'da',
  'nl',
  'en-US',
  'fr',
  'de',
  'it',
  'pt-BR',
  'es-419',
  'es-ES',
  'sv',
  'tr',
];

type Props = {
  patientId: string;
  patient: PatientProfile;
  data?: PatientOverview;
  reloadData: () => void;
};

export function PatientProgressOverviewCard({ data, patient, patientId, reloadData }: Props) {
  const intl = useIntl();
  const { locale } = useLocale();
  const contentfulLocale = getContentLocaleOrFallback(contentfulValidLocales, locale);
  const { weightUnit } = useCurrentProvider();
  const {
    starting_weight,
    current_weight,
    weight_loss_since_placement,
    perc_weight_loss_since_placement,
    starting_bmi,
    current_bmi,
    weeks_in_program,
    weeks_weighed_in,
    starting_body_fat,
    current_body_fat,
    starting_body_fat_percentage,
    current_body_fat_percentage,
    total_lean_body_mass_loss,
    starting_body_composition_reference_date,
    current_body_composition_reference_date,
    starting_reference_date,
    current_reference_date,
  } = data ?? {};

  const isStartingBodyCompositionStale =
    starting_body_composition_reference_date != null &&
    starting_body_composition_reference_date !== starting_reference_date;

  const isCurrentBodyCompositionStale =
    current_body_composition_reference_date != null &&
    current_body_composition_reference_date !== current_reference_date;

  const leanBodyMassDateRange =
    (isStartingBodyCompositionStale || isCurrentBodyCompositionStale) &&
    starting_body_composition_reference_date &&
    current_body_composition_reference_date
      ? `${toLocalizedLongDate(starting_body_composition_reference_date, locale)} → ${toLocalizedLongDate(current_body_composition_reference_date, locale)}`
      : undefined;

  const startingBodyFatStaleDate = isStartingBodyCompositionStale
    ? toLocalizedLongDate(starting_body_composition_reference_date!, locale)
    : undefined;

  const currentBodyFatStaleDate = isCurrentBodyCompositionStale
    ? toLocalizedLongDate(current_body_composition_reference_date!, locale)
    : undefined;
  const startingWeight = formatWeight(starting_weight, weightUnit);
  const currentWeight = formatWeight(current_weight, weightUnit);
  const totalWeightLoss = formatWeight(weight_loss_since_placement, weightUnit);
  const startingBodyFat = formatWeight(starting_body_fat, weightUnit);
  const currentBodyFat = formatWeight(current_body_fat, weightUnit);
  const totalLeanBodyMassLoss = formatWeight(total_lean_body_mass_loss, weightUnit);
  const isAtRisk = isPatientAtRisk(patient.at_risk_score, patient.at_risk_display);
  const shouldRenderSuccessPreditorCard = isNotNull(isAtRisk);
  const isPatientActive = patient.active_patient;

  function openOverviewNotes(): void {
    window.open(
      `https://cms.allurion.com/${contentfulLocale}/insights-patient-overview-interpretation-notes`,
      '_blank'
    );
  }

  return (
    <Card
      title={intl.formatMessage(translations.progressOverview)}
      iconButtons={[
        {
          icon: <InfoIcon />,
          onClick: openOverviewNotes,
          tooltip: intl.formatMessage(translations.overviewNotesTooltip),
        },
      ]}
    >
      {shouldRenderSuccessPreditorCard && (
        <SuccessPredictorCard
          isAtRisk={isAtRisk}
          at_risk_display={patient.at_risk_display}
          at_risk_score={patient.at_risk_score}
        />
      )}

      <div className={styles.metrics}>
        <Metric
          label={intl.formatMessage(translations.clinicOverviewStartingWeight)}
          value={startingWeight}
          action={<EditStartingWeight patientId={patientId} afterSave={reloadData} />}
        />
        <Metric
          label={intl.formatMessage(
            isPatientActive
              ? translations.clinicOverviewCurrentWeight
              : translations.clinicOverviewEndingWeight
          )}
          value={currentWeight}
        />

        <Metric
          label={intl.formatMessage(translations.clinicOverviewTotalWeightLoss)}
          value={`${perc_weight_loss_since_placement?.toFixed(1) ?? '-'}%`}
          extraValue={`${totalWeightLoss ?? '-'}`}
          dotSeparator
        />
        <Metric
          label={intl.formatMessage(translations.clinicOverviewWeightLossFromLeanBodyMass)}
          value={totalLeanBodyMassLoss}
          action={
            leanBodyMassDateRange && (
              <Tooltip text={leanBodyMassDateRange} placement="bottom">
                <InfoIcon />
              </Tooltip>
            )
          }
        />

        <Metric
          label={intl.formatMessage(translations.clinicOverviewStartingBmi)}
          value={`${starting_bmi?.toFixed(1) ?? '-'}`}
        />
        <Metric
          label={intl.formatMessage(
            isPatientActive
              ? translations.clinicOverviewCurrentBmi
              : translations.clinicOverviewEndingBmi
          )}
          value={`${current_bmi?.toFixed(1) || 0}`}
        />

        <Metric
          label={intl.formatMessage(translations.clinicOverviewStartingBodyFat)}
          value={`${starting_body_fat_percentage?.toFixed(1) ?? '-'}%`}
          extraValue={startingBodyFat}
          dotSeparator
          action={
            startingBodyFatStaleDate && (
              <Tooltip text={startingBodyFatStaleDate} placement="bottom">
                <InfoIcon />
              </Tooltip>
            )
          }
        />
        <Metric
          label={intl.formatMessage(translations.clinicOverviewCurrentBodyFat)}
          value={`${current_body_fat_percentage?.toFixed(1) ?? '-'}%`}
          extraValue={currentBodyFat}
          dotSeparator
          action={
            currentBodyFatStaleDate && (
              <Tooltip text={currentBodyFatStaleDate} placement="bottom">
                <InfoIcon />
              </Tooltip>
            )
          }
        />

        <Metric
          label={intl.formatMessage(translations.clinicOverviewWeeksInProgram)}
          value={`${weeks_in_program?.toFixed(0) ?? '-'}`}
        />
        <Metric
          label={intl.formatMessage(translations.clinicOverviewWeeksOneWeighIn)}
          value={`${weeks_weighed_in?.toFixed(0) ?? '-'}`}
        />
      </div>
    </Card>
  );
}

type MetricProps = {
  label: string;
  value: string;
  extraValue?: string;
  dotSeparator?: boolean;
  action?: React.ReactNode;
};

function Metric({ label, value, action, extraValue, dotSeparator }: MetricProps) {
  return (
    <div className={styles.metric}>
      <div className={styles.metricContent}>
        <span className={styles.metricLabel}>{label}</span>
        <span className={styles.metricValues}>
          <span className={styles.metricValue}>{value}</span>
          {extraValue && (
            <span className={styles.metricExtraValue}>
              {dotSeparator ? <>&nbsp;&nbsp;•&nbsp;&nbsp;</> : ' '}
              {extraValue}
            </span>
          )}
        </span>
      </div>
      {action && <span className={styles.metricAction}>{action}</span>}
    </div>
  );
}
